import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import { getErrorFromBackend } from "@/utils/errors";

export const FEED_PAGE_SIZE = 25;

type FetchPage<T> = (page: number, pageSize: number) => Promise<{ data: T[]; totalCount: number }>;

/**
 * Server-side "Show more": loads page 1 on mount (and again when `resetKey`
 * changes, e.g. a server-side filter) and the next page on demand. `total`
 * comes from the API; local creates/deletes adjust it via `adjustTotal` so
 * "remaining" stays right without refetching.
 *
 * Because the backend pages newest-first by id, rows created locally are
 * prepended and later pages are de-duplicated by id in case a create or
 * delete shifted the page boundaries.
 *
 * `refreshKey` is the silent sibling of `resetKey`: when it changes (e.g. a
 * live SMS event), page 1 is refetched IN PLACE — no skeleton, loaded pages
 * kept, scroll untouched — with new rows prepended and known ones replaced.
 */
export function usePagedFeed<T extends { id?: number | null }>(
  resetKey: string | number,
  fetchPage: FetchPage<T>,
  refreshKey?: string | number
) {
  const { enqueueSnackbar } = useSnackbar();
  const [items, setItems] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const nextPage = useRef(2);
  // Bumped on every reset so a "Show more" still in flight for the previous
  // key (e.g. before a filter change) is dropped instead of appended.
  const generation = useRef(0);
  const fetchRef = useRef(fetchPage);
  fetchRef.current = fetchPage;

  useEffect(() => {
    let cancelled = false;
    generation.current += 1;
    setLoading(true);
    setLoadingMore(false);
    setItems([]);
    nextPage.current = 2;
    fetchRef.current(1, FEED_PAGE_SIZE)
      .then((res) => {
        if (cancelled) return;
        setItems(res.data);
        setTotal(res.totalCount);
      })
      .catch((e) => !cancelled && enqueueSnackbar(getErrorFromBackend(e), { variant: "error" }))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [resetKey, enqueueSnackbar]);

  const lastRefresh = useRef(refreshKey);
  useEffect(() => {
    if (refreshKey === undefined || refreshKey === lastRefresh.current) return;
    lastRefresh.current = refreshKey;
    const started = generation.current;
    fetchRef.current(1, FEED_PAGE_SIZE)
      .then((res) => {
        if (generation.current !== started) return;
        setTotal(res.totalCount);
        setItems((prev) => {
          const fresh = new Map(res.data.map((i) => [i.id, i]));
          const known = new Set(prev.map((i) => i.id));
          return [...res.data.filter((i) => !known.has(i.id)), ...prev.map((i) => fresh.get(i.id) ?? i)];
        });
      })
      // A silent refresh failing keeps what's on screen.
      .catch(() => undefined);
  }, [refreshKey]);

  const loadMore = useCallback(async () => {
    if (loadingMore) return;
    const started = generation.current;
    const stale = () => generation.current !== started;
    setLoadingMore(true);
    try {
      const res = await fetchRef.current(nextPage.current, FEED_PAGE_SIZE);
      if (stale()) return;
      nextPage.current += 1;
      setTotal(res.totalCount);
      setItems((prev) => {
        const seen = new Set(prev.map((i) => i.id));
        return [...prev, ...res.data.filter((i) => !seen.has(i.id))];
      });
    } catch (e) {
      if (!stale()) enqueueSnackbar(`Couldn't load more: ${getErrorFromBackend(e)}`, { variant: "error" });
    } finally {
      if (!stale()) setLoadingMore(false);
    }
  }, [loadingMore, enqueueSnackbar]);

  const adjustTotal = useCallback((delta: number) => setTotal((t) => Math.max(0, t + delta)), []);

  return {
    items,
    setItems: setItems as Dispatch<SetStateAction<T[]>>,
    total,
    remaining: Math.max(0, total - items.length),
    loading,
    loadingMore,
    loadMore,
    adjustTotal,
  };
}
